import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import Table from '@amzn/awsui-components-console/table';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Button from '@amzn/awsui-components-console/button';
import Header from '@amzn/awsui-components-console/header';
import Pagination from '@amzn/awsui-components-console/pagination';
import Flashbar, { type FlashbarProps } from '@amzn/awsui-components-console/flashbar';
import type { SelectProps } from '@amzn/awsui-components-console/select';
import type { Collection } from '@amzn/aws-gcr-content-connection-management-client';
import { DataSource } from '@amzn/aws-gcr-content-connection-management-client';
import { useCollections } from '../../HomePage/hooks/useCollections';
import CollectionModal, { type FormData } from './CollectionModal';
import type { SyncFrequency } from './SyncFrequencyEditor';
import TableFilters from './TableFilters';
import { syncFrequencyToCron, cronToSyncFrequency } from '../../../utils/cronUtils';
import { portalService } from '../../HomePage/services/portalService';
import { getCollectionColumnDefinitions, filterCollections, sortCollections } from '../../../shared/config/collectionsTableConfig';
import { useFederate } from '../../../shared/contexts/FederateContext';
import { isAdminUser } from '../../../utils/roleUtils';

const CollectionsTable: React.FC = () => {
  const { userRoles, userLogin } = useFederate();
  const isAdmin = isAdminUser(userRoles);
  const { collections: collectionsData, loading, refetch } = useCollections();

  const [searchParams] = useSearchParams();
  const sourceParam = searchParams.get('source');
  const searchParam = searchParams.get('search');

  const [selectedItems, setSelectedItems] = React.useState<Collection[]>([]);
  const [filteringText, setFilteringText] = React.useState(searchParam || '');
  const [currentPageIndex, setCurrentPageIndex] = React.useState(1);
  const [selectedDataSource, setSelectedDataSource] = React.useState<SelectProps.Option | null>(
    sourceParam ? { label: sourceParam, value: sourceParam } : { label: 'All data sources', value: '' },
  );
  const [sortingColumn, setSortingColumn] = React.useState<{
    sortingField: string;
    isDescending: boolean;
  }>({
    sortingField: 'createdAt',
    isDescending: true,
  });

  // Modal state
  const [isModalVisible, setIsModalVisible] = React.useState(false);
  const [isEditMode, setIsEditMode] = React.useState(false);

  const getDefaultSyncFrequency = (): SyncFrequency => ({
    type: 'daily',
    minute: 0,
    hour: 0,
  });

  const [formData, setFormData] = React.useState<FormData>({
    name: '',
    url: '',
    description: '',
    source: 'Quip',
    syncFrequency: getDefaultSyncFrequency(),
  });

  // Flashbar state
  const [flashbarItems, setFlashbarItems] = React.useState<FlashbarProps.MessageDefinition[]>([]);

  // Fetch collections and applications
  // Use collections directly without adding applicationCount
  const collections = collectionsData;

  // Get unique data sources
  const dataSourceOptions = React.useMemo(() => {
    const uniqueSources = Array.from(new Set(collections.map((c) => c.source))).sort();
    const options = uniqueSources.map((source) => ({ label: source, value: source }));
    return [{ label: 'All data sources', value: '' }, ...options];
  }, [collections]);

  // Filter collections
  const filteredItems = filterCollections(collections, filteringText, selectedDataSource?.value || null);

  // Sort collections
  const sortedItems = sortCollections(filteredItems, sortingColumn.sortingField, sortingColumn.isDescending);

  // Pagination
  const pageSize = 10;
  const paginatedItems = sortedItems.slice((currentPageIndex - 1) * pageSize, currentPageIndex * pageSize);
  const totalPages = Math.ceil(sortedItems.length / pageSize);

  // Flash message helper
  const showFlashMessage = (type: 'success' | 'error', message: string) => {
    const id = Date.now().toString();
    setFlashbarItems([
      {
        type,
        content: message,
        dismissible: true,
        dismissLabel: 'Dismiss message',
        onDismiss: () => setFlashbarItems((items) => items.filter((item) => item.id !== id)),
        id,
      },
    ]);
  };

  // Modal handlers
  const handleOpenModal = () => {
    setIsEditMode(false);
    setIsModalVisible(true);
  };

  const handleOpenEditModal = () => {
    if (selectedItems.length === 1) {
      const item = selectedItems[0];
      setFormData({
        name: item.name || '',
        url: item.collectionId || '',
        description: item.description || '',
        source: item.source || '',
        syncFrequency: cronToSyncFrequency(item.schedule || ''),
      });
      setIsEditMode(true);
      setIsModalVisible(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    // Reset form
    setFormData({
      name: '',
      url: '',
      description: '',
      source: 'Quip',
      syncFrequency: getDefaultSyncFrequency(),
    });
  };

  const handleCreateCollection = async () => {
    try {
      const cronExpression = syncFrequencyToCron(formData.syncFrequency);
      await portalService.createCollection({
        collectionId: formData.url,
        name: formData.name,
        description: formData.description,
        source: formData.source as DataSource,
        schedule: cronExpression,
        requester: userLogin || 'unknown-user',
      });
      handleCloseModal();
      showFlashMessage('success', 'Content Collection created successfully.');
      // Refresh collections data
      await refetch();
    } catch (error) {
      console.error('Failed to create collection:', error);
      showFlashMessage('error', 'Failed to create Content Collection. Please try again.');
    }
  };

  const handleEditCollection = async () => {
    try {
      const cronExpression = syncFrequencyToCron(formData.syncFrequency);
      await portalService.updateCollection({
        collectionId: selectedItems[0].collectionId!,
        name: formData.name,
        description: formData.description,
        source: formData.source as DataSource,
        schedule: cronExpression,
        requester: userLogin || 'unknown-user',
      });
      handleCloseModal();
      showFlashMessage('success', 'Content Collection updated successfully.');
      // Refresh collections data
      await refetch();
    } catch (error) {
      console.error('Failed to update collection:', error);
      showFlashMessage('error', 'Failed to update Content Collection. Please try again.');
    }
  };

  return (
    <>
      {flashbarItems.length > 0 && (
        <Box margin={{ bottom: 'l' }}>
          <Flashbar items={flashbarItems} />
        </Box>
      )}
      <Table
        renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) =>
          `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
        }
        onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
        selectedItems={selectedItems}
        sortingColumn={{ sortingField: sortingColumn.sortingField }}
        sortingDescending={sortingColumn.isDescending}
        onSortingChange={({ detail }) => {
          setSortingColumn({
            sortingField: detail.sortingColumn?.sortingField || 'createdAt',
            isDescending: detail.isDescending || false,
          });
        }}
        columnDefinitions={getCollectionColumnDefinitions()}
        columnDisplay={[
          { id: 'name', visible: true },
          { id: 'description', visible: true },
          { id: 'dataSource', visible: true },
          { id: 'itemCount', visible: true },
          { id: 'applicationCount', visible: true },
          { id: 'creator', visible: true },
          { id: 'createTime', visible: true },
          { id: 'lastUpdateTime', visible: true },
          { id: 'lastUpdatedBy', visible: true },
          { id: 'syncFrequency', visible: true },
        ]}
        enableKeyboardNavigation
        resizableColumns
        items={paginatedItems}
        loading={loading}
        loadingText="Loading collections"
        selectionType="multi"
        trackBy="collectionId"
        stickyColumns={{ first: 1, last: 0 }}
        empty={
          <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
            <SpaceBetween size="m">
              <b>No collections</b>
              <Button
                disabled={!isAdmin}
                disabledReason={!isAdmin ? 'Admin role required to create collections' : undefined}
                onClick={handleOpenModal}
              >
                Create collection
              </Button>
            </SpaceBetween>
          </Box>
        }
        filter={
          <TableFilters
            filteringText={filteringText}
            selectedDataSource={selectedDataSource}
            dataSourceOptions={dataSourceOptions}
            matchCount={sortedItems.length}
            onFilteringTextChange={(text) => {
              setFilteringText(text);
              setCurrentPageIndex(1);
            }}
            onDataSourceChange={(option) => {
              setSelectedDataSource(option);
              setCurrentPageIndex(1);
            }}
          />
        }
        header={
          <Header
            counter={selectedItems.length ? `(${selectedItems.length}/${collections.length})` : `(${collections.length})`}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  disabled={!isAdmin || selectedItems.length !== 1}
                  disabledReason={!isAdmin ? 'Admin role required to edit collections' : undefined}
                  onClick={handleOpenEditModal}
                >
                  Edit
                </Button>
                <div className="bright-yellow-button">
                  <Button
                    variant="primary"
                    disabled={!isAdmin}
                    disabledReason={!isAdmin ? 'Admin role required to create collections' : undefined}
                    onClick={handleOpenModal}
                  >
                    Create collection
                  </Button>
                </div>
              </SpaceBetween>
            }
          >
            Collections
          </Header>
        }
        pagination={
          <Pagination
            currentPageIndex={currentPageIndex}
            pagesCount={totalPages}
            onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
          />
        }
      />

      <CollectionModal
        visible={isModalVisible}
        isEditMode={isEditMode}
        formData={formData}
        onDismiss={handleCloseModal}
        onSubmit={isEditMode ? handleEditCollection : handleCreateCollection}
        onFormDataChange={setFormData}
      />
    </>
  );
};

export default CollectionsTable;
